import React, { useState, useEffect } from 'react';
import {
  Card,
  Dropdown,
  Row,
  Col,
  ButtonGroup,
  Button,
  Modal
} from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import Flex from 'components/common/Flex';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import CardDropdown from 'components/common/CardDropdown';
import PageHeader from 'components/common/PageHeader';
import { getAll, deleteSurvey } from 'services/surveys'; // Importa la función para obtener las encuestas
import { toast } from 'react-toastify';

const columns = (handleEdit, handleDelete, handleShares) => [
  {
    accessorKey: 'none',
    header: 'Opciones',
    enableSorting: false,
    meta: {
      headerProps: { className: 'text-center' },
      cellProps: { className: 'text-center' }
    },
    cell: ({ row: { original } }) => {
      return (
        <CardDropdown drop={'end'}>
          <div className="py-2">
            <Dropdown.Item onClick={() => handleEdit(original.id)}>
              Editar
            </Dropdown.Item>
            <Dropdown.Item onClick={() => handleShares(original.id)}>
              Reportes Compartidos
            </Dropdown.Item>
            <Dropdown.Item
              onClick={() => handleDelete(original.id)} // Agrega la opción de eliminar
              className="text-danger"
            >
              Eliminar
            </Dropdown.Item>
          </div>
        </CardDropdown>
      );
    }
  },
  {
    accessorKey: 'name',
    header: 'Nombre',
    meta: {
      headerProps: { className: 'text-center text-900' },
      cellProps: { className: 'text-center py-2' }
    },
    cell: ({ row: { original } }) => {
      const { name } = original;
      return (
        <Link to="#!">
          <Flex alignItems="center" className="justify-content-center">
            <div className="flex-1">
              <h5 className="mb-0 fs-10">{name}</h5>
            </div>
          </Flex>
        </Link>
      );
    }
  },
  {
    accessorKey: 'description',
    header: 'Descripción',
    meta: {
      headerProps: { className: 'text-center text-900' },
      cellProps: { className: 'text-center' }
    },
    cell: ({ row: { original } }) => {
      const { description } = original;
      return <span>{description}</span>;
    }
  },
  {
    accessorKey: 'site.name',
    header: 'Sitio',
    meta: {
      headerProps: { className: 'text-center text-900' },
      cellProps: { className: 'text-center' }
    },
    cell: ({ row: { original } }) => {
      return <span>{original.site?.name || '-'}</span>;
    }
  },
  {
    accessorKey: 'is_active',
    header: 'Estado',
    meta: {
      headerProps: { className: 'text-center text-900' },
      cellProps: { className: 'text-center' }
    },
    cell: ({ row: { original } }) => {
      const { is_active } = original;
      return <span>{is_active ? 'Activo' : 'Inactivo'}</span>;
    }
  },
  {
    accessorKey: 'created_at',
    header: 'Fecha de Creación',
    meta: {
      headerProps: { className: 'text-center text-900' },
      cellProps: { className: 'text-center' }
    }
  }
];

const SurveysList = () => {
  const [surveys, setSurveys] = useState([]); // Estado para almacenar las encuestas
  const [loading, setLoading] = useState(true); // Estado para manejar la carga
  const [filter, setFilter] = useState(1); // Estado para manejar el filtro (1: activos, 0: inactivos)
  const [showDelete, setShowDelete] = useState(false); // Estado para manejar la modal de eliminación
  const [toDeleteId, setToDeleteId] = useState(null); // Estado para almacenar el id de la encuesta a eliminar
  const navigate = useNavigate(); // Hook para redirigir

  useEffect(() => {
    // Llama a la API para obtener las encuestas según el filtro
    setLoading(true); // Inicia la carga
    getAll(filter)
      .then(response => {
        if (response.result) {
          console.log('Encuestas obtenidas:', response.surveys);
          setSurveys(response.surveys); // Actualiza el estado con los datos obtenidos
        }
        setLoading(false); // Finaliza la carga
      })
      .catch(error => {
        console.error('Error al obtener las encuestas:', error);
        setLoading(false); // Finaliza la carga incluso si hay un error
      });
  }, [filter]); // Ejecuta el efecto cuando cambia el filtro

  const handleEdit = id => {
    localStorage.setItem('selectedSurveyId', id); // Guarda el id en localStorage
    navigate(`/surveys/edit`); // Redirige a la página de edición
  };

  const handleShares = id => {
    navigate(`/surveys/shares?id=${id}`); // Redirige a la página de gestión de reportes compartidos
  };

  const handleDelete = id => {
    setToDeleteId(id); // Establece el id de la encuesta a eliminar
    setShowDelete(true); // Muestra la modal de confirmación
  };

  const confirmDelete = async () => {
    if (!toDeleteId) return;
    try {
      const result = await deleteSurvey(toDeleteId);
      if (result.result) {
        setLoading(true);
        getAll(filter)
          .then(response => {
            if (response.result) {
              setSurveys(response.surveys);
              toast.success('Encuesta eliminada correctamente');
            }
            setLoading(false);
          })
          .catch(() => setLoading(false));
      } else {
        toast.error('No se pudo eliminar la encuesta.');
      }
    } catch (error) {
      toast.error('Ocurrió un error al eliminar la encuesta.');
    }
    setShowDelete(false);
    setToDeleteId(null);
  };

  const table = useAdvanceTable({
    data: surveys,
    columns: columns(handleEdit, handleDelete, handleShares), // Pasa handleEdit, handleDelete y handleShares a las columnas
    sortable: true, // Habilita el ordenamiento
    pagination: true, // Habilita la paginación
    perPage: 5 // Número de filas por página
  });

  return (
    <>
      <PageHeader
        title="Encuestas"
        description="Gestión de encuestas en el sistema"
        className="mb-3"
      />

      <AdvanceTableProvider {...table}>
        <Card className="mb-3">
          <Card.Header>
            <Row className="g-3 align-items-center">
              <Col xs={12} md={6} lg={4}>
                <h5 className="mb-0 text-center text-md-start">
                  Gestión de Encuestas
                </h5>
              </Col>
              <Col xs={12} md="auto" className="text-center">
                {/* Botones para cambiar el filtro */}
                <ButtonGroup size="sm">
                  {' '}
                  {/* Tamaño pequeño */}
                  <Button
                    variant={filter === 1 ? 'primary' : 'outline-primary'}
                    onClick={() => setFilter(1)}
                  >
                    Activas
                  </Button>
                  <Button
                    variant={filter === 0 ? 'primary' : 'outline-primary'}
                    onClick={() => setFilter(0)}
                  >
                    Inactivas
                  </Button>
                </ButtonGroup>
              </Col>
              <Col xs={12} md="auto" className="text-center">
                {/* Botón para agregar encuesta */}
                <Button
                  variant="success"
                  size="sm" // Tamaño pequeño
                  onClick={
                    () => navigate('/surveys/add') // Redirige a la página de agregar encuesta
                  }
                >
                  Agregar Encuesta
                </Button>
              </Col>
              <Col xs={12} md={6} lg={4} className="text-center text-md-end">
                <AdvanceTableSearchBox />
              </Col>
            </Row>
          </Card.Header>
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center py-3">Cargando encuestas...</div>
            ) : (
              <AdvanceTable
                headerClassName="bg-200 text-nowrap align-middle"
                rowClassName="align-middle white-space-nowrap"
                tableProps={{
                  size: 'sm',
                  striped: true,
                  bordered: true, // Bootstrap: agrega bordes a todas las celdas
                  className:
                    'fs-10 mb-0 overflow-hidden table-bordered table-hover', // table-bordered y table-hover para mejor diseño
                  style: {
                    borderCollapse: 'separate',
                    borderSpacing: 0
                  }
                }}
              />
            )}
          </Card.Body>
          <Card.Footer>
            <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
          </Card.Footer>
        </Card>
      </AdvanceTableProvider>

      {/* Modal para confirmar eliminación */}
      <Modal show={showDelete} onHide={() => setShowDelete(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar eliminación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Estás seguro de que deseas eliminar esta encuesta? Esta acción no se
          puede deshacer.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDelete(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Eliminar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SurveysList;
